import { NavLink } from "react-router-dom";
import Icon from "./Icon";
import { initials } from "../utils/formatters";
import { useAuth } from "../contexts/AuthContext";

const ADMIN_NAV = [
  {
    section: "Workspace",
    items: [
      { to: "/admin", icon: "dashboard", label: "Dashboard", end: true },
      { to: "/admin/calendar", icon: "calendar", label: "Calendar" },
      { to: "/admin/todos", icon: "list", label: "Todos" },
      { to: "/admin/notifications", icon: "bell", label: "Activity" },
    ],
  },
  {
    section: "Clients",
    items: [
      { to: "/admin/clients", icon: "users", label: "Clients" },
      { to: "/admin/client-prefixes", icon: "list", label: "Client ID Prefixes" },
    ],
  },
  {
    section: "Knowledge Base",
    items: [
      { to: "/admin/org-kb/drive", icon: "folder", label: "Org Drive" },
      { to: "/admin/org-kb/fields", icon: "lock", label: "Org Fields" },
    ],
  },
  {
    section: "Tasks",
    items: [
      { to: "/admin/oversight", icon: "check-square", label: "Task Oversight" },
      { to: "/admin/task-masters", icon: "layers", label: "Task Masters" },
      { to: "/admin/task-groups", icon: "folder", label: "Task Groups" },
    ],
  },
  {
    section: "Support",
    items: [
      { to: "/admin/tickets", icon: "message-circle", label: "Tickets" },
      { to: "/admin/chat", icon: "message-circle", label: "Chat", requireAddon: ["chat", "chat_extended"] },
    ],
  },
  {
    section: "Payments",
    items: [
      { to: "/admin/payments", icon: "receipt", label: "Oversight", end: true },
      { to: "/admin/payments/masters", icon: "dollar-sign", label: "Templates" },
      { to: "/admin/invoices", icon: "file-text", label: "Invoices" },
    ],
  },
  {
    section: "System",
    items: [
      { to: "/admin/firm-profiles", icon: "briefcase", label: "Firm Profiles" },
      { to: "/admin/subscription", icon: "receipt", label: "Subscription" },
      { to: "/admin/ops", icon: "zap", label: "Ops Runner" },
      { to: "/admin/server", icon: "hard-drive", label: "Usage & Limits" },
      { to: "/admin/changelog", icon: "sparkles", label: "What's new" },
    ],
  },
  {
    section: "Team",
    items: [
      { to: "/admin/employees", icon: "user", label: "Employees" },
      { to: "/admin/admins", icon: "users", label: "Admins" },
      { to: "/admin/departments", icon: "briefcase", label: "Departments" },
      { to: "/admin/geofences", icon: "map-pin", label: "Geofences", requireAddon: ["geofence"] },
    ],
  },
  {
    section: "Attendance",
    items: [
      { to: "/admin/attendance", icon: "clock", label: "Register", end: true, requireAddon: ["attendance"] },
      { to: "/admin/attendance/reports", icon: "file-text", label: "Reports", requireAddon: ["attendance"] },
      { to: "/admin/leaves", icon: "calendar", label: "Leaves", requireAddon: ["attendance"] },
      { to: "/admin/my-attendance", icon: "check-circle", label: "My Attendance", requireAddon: ["attendance"] },
      { to: "/admin/my-leaves", icon: "calendar", label: "My Leaves", requireAddon: ["attendance"] },
    ],
  },
];

const EMPLOYEE_NAV = [
  {
    section: "My Work",
    items: [
      { to: "/me", icon: "check-square", label: "My Tasks", end: true },
      { to: "/me/todos", icon: "list", label: "Todos" },
      { to: "/me/calendar", icon: "calendar", label: "Calendar" },
      { to: "/me/history", icon: "clock", label: "History" },
      { to: "/me/attendance", icon: "check-circle", label: "My Attendance", requireAddon: ["attendance"] },
      { to: "/me/leaves", icon: "calendar", label: "My Leaves", requireAddon: ["attendance"] },
    ],
  },
  {
    section: "Clients",
    // Visible only when the employee has at least one client-* permission.
    // The router still owns auth enforcement; this just hides the entry
    // point from people who can't use it.
    items: [
      {
        to: "/me/clients",
        icon: "users",
        label: "Clients",
        requireAny: ["client.view", "client.add", "client.edit"],
      },
    ],
  },
  {
    section: "Knowledge Base",
    items: [
      {
        to: "/me/org-kb/drive",
        icon: "folder",
        label: "Org Drive",
        requireAny: ["org-kb.view", "org-kb.edit", "org-kb.delete"],
      },
      {
        to: "/me/org-kb/fields",
        icon: "lock",
        label: "Org Fields",
        requireAny: ["org-kb.view", "org-kb.edit", "org-kb.delete"],
      },
    ],
  },
  {
    section: "Support",
    items: [
      { to: "/me/tickets", icon: "message-circle", label: "Assigned Tickets" },
      { to: "/me/chat", icon: "message-circle", label: "Chat", requireAddon: ["chat", "chat_extended"] },
    ],
  },
  {
    section: "Payments",
    items: [
      {
        to: "/me/payments",
        icon: "receipt",
        label: "Oversight",
        requireAny: ["payments.view", "payments.edit"],
      },
      {
        to: "/me/invoices",
        icon: "file-text",
        label: "Invoices",
        requireAny: ["payments.view", "payments.edit"],
      },
    ],
  },
];

const CUSTOMER_NAV = [
  {
    section: "My Account",
    items: [
      { to: "/portal", icon: "home", label: "Overview", end: true },
      { to: "/portal/tickets", icon: "message-circle", label: "Support Tickets" },
      { to: "/portal/files", icon: "folder", label: "Documents" },
    ],
  },
];

const SUPER_NAV = [
  {
    section: "Platform",
    items: [
      { to: "/super", icon: "dashboard", label: "Overview", end: true },
      { to: "/super/firms", icon: "building", label: "Firms" },
      { to: "/super/addons", icon: "layers", label: "Addons" },
      { to: "/super/announcements", icon: "sparkles", label: "Announcements" },
      { to: "/super/leads", icon: "inbox", label: "Leads" },
      { to: "/super/active-users", icon: "activity", label: "Active users" },
      { to: "/super/sessions", icon: "log-out", label: "Sessions" },
      { to: "/super/server", icon: "hard-drive", label: "Server" },
    ],
  },
];

const NAV_BY_ROLE = {
  super_admin: SUPER_NAV,
  admin: ADMIN_NAV,
  employee: EMPLOYEE_NAV,
  customer: CUSTOMER_NAV,
};

const ROLE_LABEL = {
  super_admin: "Platform",
  admin: "Admin",
  employee: "Employee",
  customer: "Customer",
};

export default function Sidebar({
  role,
  collapsed,
  mobileOpen = false,
  onCloseMobile,
  onToggleCollapse,
  user,
  badges = {},
}) {
  const { hasAnyPermission, hasAddon } = useAuth();
  // Filter each section's items by requireAny (permissions) and requireAddon
  // (subscription addons) — sections that end up empty collapse to nothing so
  // we never render a heading with no children.
  const nav = (NAV_BY_ROLE[role] || [])
    .map((sec) => ({
      ...sec,
      items: sec.items.filter(
        (it) =>
          (!it.requireAny || hasAnyPermission(...it.requireAny)) &&
          (!it.requireAddon || hasAddon(...it.requireAddon)),
      ),
    }))
    .filter((sec) => sec.items.length > 0);
  const roleLabel = ROLE_LABEL[role] || "User";

  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}>
      <div className="sb-brand">
        <div className="sb-brand-mark">
          <img src="/logo-mark.svg" alt="Devsoleil" />
        </div>
        <div className="sb-brand-text">
          Smart CA
          <small>{roleLabel} Console</small>
        </div>
      </div>
      <div className="sb-nav">
        {nav.map((sec) => (
          <div key={sec.section}>
            <div className="sb-section-label">{sec.section}</div>
            {sec.items.map((it) => {
              const badge = badges[it.to];
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  title={it.label}
                  onClick={onCloseMobile}
                  className={({ isActive }) => `sb-item ${isActive ? "active" : ""}`}
                >
                  <span className="sb-icon">
                    <Icon name={it.icon} size={16} />
                  </span>
                  <span className="sb-label">{it.label}</span>
                  {badge != null && !collapsed && <span className="sb-badge">{badge}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}
      </div>
      <div className="sb-footer">
        <div className="avatar avatar-sm">{initials(user?.name)}</div>
        <div className="sb-user-info">
          <div className="sb-user-name">{user?.name || "User"}</div>
          <div className="sb-user-role">{roleLabel}</div>
        </div>
        <button className="sb-toggle" onClick={onToggleCollapse} title="Collapse">
          <Icon name="chevron-left" size={14} />
        </button>
      </div>
    </aside>
  );
}
